import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlusCircle, Save2, Trash, Calendar3, Receipt, Coin, CashCoin, Bank, Hospital, Diagram3, HandThumbsUp, CardList, ListTask, PersonLinesFill, Copy } from 'react-bootstrap-icons'
import { Button, Form, Row, Col } from 'react-bootstrap'
import SearchSelect from '../components/form/SearchSelect'
import InputNumber from '../components/form/InputNumber'
import InputDate from '../components/form/InputDate'
import PayrollsService from '../services/PayrollsService'
import UsersService from '../services/UsersService'
import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"

const PayrollsForm = (props) => {

    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [userId, setUserId] = useState(props.userId)
    const [usersList, setUsersList] = useState([])
    const [userErrorKey, setUserErrorKey] = useState('')
    const [netSalary, setNetSalary] = useState('')
    const [irpf, setIrpf] = useState('')
    const [ss, setSs] = useState('')
    const [grossSalary, setGrossSalary] = useState('')
    const [date, setDate] = useState(formatDate(Date.now(), 'YYYY-MM-DD'))
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)
    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    // Fetch users
    const fetchUsers = async () => {
        try {
            const response = await UsersService.getUsers(1, '', 2000)
            setUsersList(response.data.users)
        } catch (err) {
            setError(err)
        }
    }

    const getData = async () => {
        await PayrollsService.getPayroll(elementId).then(
            response => {
                setUserId(response.data.userId || '')
                setNetSalary(response.data.netSalary || '')
                setIrpf(response.data.irpf || '')
                setSs(response.data.ss || '')
                setGrossSalary(response.data.grossSalary || '')
                setDate(formatDate(response.data.date, 'YYYY-MM-DD'))
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {
        fetchUsers()

        if (elementId) {
            getData()
        }

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            case 'duplicate':
                setButtonTitle(<FormattedMessage id="textDuplicate" defaultMessage="Duplicate" />)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = () => {
        props.onSubmit()
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value

        if (name === 'userId') {
            setUserErrorKey('')
        }
        setFieldValue(name, value)
    }

    const setFieldValue = (fieldName, value) => {
        switch (fieldName) {
            case 'userId':
                setUserId(value)
                break
            case 'netSalary':
                setNetSalary(value)
                break
            case 'irpf':
                setIrpf(value)
                break
            case 'ss':
                setSs(value)
                break
            case 'grossSalary':
                setGrossSalary(value)
                break
            case 'date':
                setDate(value)
                break
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        setUserErrorKey('')

        const form = e.currentTarget
        let finalUserId = userId

        if (action !== "delete") {
            const matchedUser = usersList.find(u => 
                u.name.toLowerCase() === (userId?.toString().toLowerCase() || '') || 
                u._id === userId
            )
            if (matchedUser){
                finalUserId = matchedUser._id
            } 

            const isUserEmpty = !userId || userId.toString().trim() === ''
            const isUserInvalid = !isUserEmpty && !/^[0-9a-fA-F]{24}$/.test(finalUserId)

            if (!form.checkValidity() || isUserInvalid || isUserEmpty) {
                setFormValid(true)
                if (isUserEmpty) {
                    setUserErrorKey('payrollsUserInvalid') 
                } 
                else if (isUserInvalid) {
                    setUserErrorKey('payrollsUsersNotFound') 
                }
                return
            }
        }

        const payroll = {
            userId: finalUserId,
            netSalary,
            irpf,
            ss,
            grossSalary,
            date
        }

        try {
            if (elementId && action === "edit") {
                await PayrollsService.updatePayroll(payroll, elementId)
            } else if (action === "add" || action === "duplicate") {
                await PayrollsService.addPayroll(payroll)
            } else if (action === "delete") {
                await PayrollsService.deletePayroll(elementId)
            }

            setFormValid(false)
            onSubmit()
        } catch (error) {
            setShowMsg({ text: error || "An error occurred", style: 'danger' })
            setShowAlert(true)
        }
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-4" controlId="id">
                        <Form.Label className='fw-semibold'>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}

                <Form.Group className="mb-4" controlId="userId">
                    <PersonLinesFill /><Form.Label className="fw-semibold mb-2 ms-2">
                        <FormattedMessage id="payrollsUser" defaultMessage="User" />:
                    </Form.Label>

                    <SearchSelect
                        fieldName={'userId'}
                        selected={userId}
                        onChange={handleInputChange}
                        options={usersList}
                        disabled={inputDisabled}
                        className={userErrorKey !== '' ? 'is-invalid border-danger' : ''}
                    />

                    {userErrorKey !== '' && (
                        <div className="text-danger small mt-1">
                            <FormattedMessage id={userErrorKey} />
                        </div>
                    )}
                </Form.Group>

                <Row className="mt-4">
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="netSalary">
                            <Coin /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="payrollsNetSalary" defaultMessage="Net salary" />:</Form.Label>
                            <InputNumber
                                fieldName={"netSalary"}
                                value={netSalary}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            //required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="payrollsNetSalaryInvalid" defaultMessage="Provide a net salary" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="irpf">
                            <Bank /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="payrollsIrpf" defaultMessage="IRPF" />:</Form.Label>
                            <InputNumber
                                fieldName={"irpf"}
                                value={irpf}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            //required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="payrollsIrpfInvalid" defaultMessage="Provide an IRPF" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="ss">
                            <Hospital /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="payrollsSs" defaultMessage="SS" />:</Form.Label>
                            <InputNumber
                                fieldName={"ss"}
                                value={ss}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                            //required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="payrollsSsInvalid" defaultMessage="Provide a SS" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mt-4 mb-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="date">
                            <Calendar3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="payrollsDate" defaultMessage="Payroll date" />:</Form.Label>
                            <InputDate
                                fieldName='date'
                                value={date}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="payrollsDateInvalid" defaultMessage="Provide an payroll date" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="grossSalary">
                            <CashCoin /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="payrollsGrossSalary" defaultMessage="Gross salary" />:</Form.Label>
                            <InputNumber
                                fieldName={"grossSalary"}
                                value={grossSalary}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="payrollsGrossSalaryInvalid" defaultMessage="Provide a gross salary" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark fw-semibold"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary fw-semibold"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger fw-semibold"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}

                    {action === "duplicate" && (
                        <Button className="btn btn-dark fw-semibold" type="submit">
                            <Copy /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    )

}

export default PayrollsForm